import { useState, useEffect, useCallback, useRef } from 'react';
import { getClientSyncData, triggerClientSync, updateClientSyncSchedule } from '../services/api';
import { ClientSyncData, GetSyncDataResponse, TriggerSyncResponse, Staleness } from '../types';
import toast from 'react-hot-toast';

// Reserved route segments that should not be treated as client IDs
const RESERVED_ROUTES = ['onboard', 'new', 'create', 'edit'];

interface UseClientSyncDataResult {
  syncData: ClientSyncData | null;
  loading: boolean;
  syncing: boolean;
  error: string | null;
  staleness: Staleness;
  needsSync: boolean;
  refresh: () => Promise<void>;
  triggerSync: () => Promise<TriggerSyncResponse | null>;
  updateSchedule: (enabled: boolean, intervalHours: number) => Promise<boolean>;
}

export function useClientSyncData(clientId: string | null): UseClientSyncDataResult {
  const [syncData, setSyncData] = useState<ClientSyncData | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staleness, setStaleness] = useState<Staleness>('never_synced');
  const [needsSync, setNeedsSync] = useState(true);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncStartTimeRef = useRef<string | null>(null);
  const syncingRef = useRef(false); // Track syncing state for timeout callback

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Fetch cached sync data
  const refresh = useCallback(async () => {
    if (!clientId || RESERVED_ROUTES.includes(clientId.toLowerCase())) return;

    setLoading(true);
    setError(null);

    try {
      const response: GetSyncDataResponse = await getClientSyncData(clientId);

      if (response.success) {
        setSyncData(response.data);
        setStaleness(response.staleness);
        setNeedsSync(response.needsSync);

        // Check if sync completed (new data arrived after we started syncing)
        if (syncing && syncStartTimeRef.current && response.data?.lastFullSyncCompleted) {
          const syncStartTime = new Date(syncStartTimeRef.current).getTime();
          const lastSyncTime = new Date(response.data.lastFullSyncCompleted).getTime();

          if (lastSyncTime > syncStartTime) {
            // Sync completed!
            stopPolling();
            setSyncing(false);
            syncingRef.current = false;
            syncStartTimeRef.current = null;
            toast.dismiss('sync-polling');

            const duration = response.data.lastSyncDurationMs
              ? Math.round(response.data.lastSyncDurationMs / 1000)
              : '?';

            if (response.data.syncStatus === 'complete') {
              toast.success(`Sync completed successfully (${duration}s)`, { duration: 5000 });
            } else if (response.data.syncStatus === 'partial') {
              toast.success(`Sync completed with some errors (${duration}s)`, { duration: 5000, icon: '⚠️' });
            } else {
              toast.error(`Sync failed`, { duration: 5000 });
            }
          }
        }
      } else {
        setError('Failed to load sync data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sync data');
    } finally {
      setLoading(false);
    }
  }, [clientId, syncing, stopPolling]);

  // Trigger a full sync (fire-and-forget with polling)
  const triggerSync = useCallback(async (): Promise<TriggerSyncResponse | null> => {
    if (!clientId || RESERVED_ROUTES.includes(clientId.toLowerCase())) return null;

    setSyncing(true);
    syncingRef.current = true;
    setError(null);
    syncStartTimeRef.current = new Date().toISOString();

    // Show starting toast
    toast.loading('Sync started... This may take 1-2 minutes.', {
      id: 'sync-started',
      duration: 5000
    });

    // Start polling for status every 10 seconds
    stopPolling();
    pollingRef.current = setInterval(() => {
      refresh();
    }, 10000);

    // Also set a timeout to stop polling after 3 minutes (safety)
    setTimeout(() => {
      if (syncingRef.current) {
        stopPolling();
        setSyncing(false);
        syncingRef.current = false;
        toast.dismiss('sync-started');
        toast.dismiss('sync-polling');
        toast('Sync is taking longer than expected. Check back later.', {
          icon: '⏳',
          duration: 5000
        });
      }
    }, 180000);

    try {
      // Fire the sync request - don't wait for response (it will timeout anyway)
      const response = await triggerClientSync(clientId);

      // If we get here, sync completed within timeout!
      stopPolling();
      setSyncing(false);
      syncingRef.current = false;
      await refresh();
      toast.dismiss('sync-started');
      toast.dismiss('sync-polling');

      return response;
    } catch (err) {
      // Timeout or other error - sync may still be running in background
      // Keep polling, don't show error yet
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';

      // Only show error if it's NOT a timeout (500 from proxy timeout)
      if (!errorMsg.includes('500') && !errorMsg.includes('timeout')) {
        setError(errorMsg);
        stopPolling();
        setSyncing(false);
        syncingRef.current = false;
        toast.dismiss('sync-started');
        toast.dismiss('sync-polling');
        toast.error(`Sync failed: ${errorMsg}`);
      } else {
        // It's likely a timeout - keep polling
        toast.dismiss('sync-started');
        toast.loading('Sync in progress... (polling for status)', {
          id: 'sync-polling',
          duration: 30000
        });
      }

      return null;
    }
  }, [clientId, refresh, syncing, stopPolling]);

  // Update sync schedule
  const updateSchedule = useCallback(async (enabled: boolean, intervalHours: number): Promise<boolean> => {
    if (!clientId || RESERVED_ROUTES.includes(clientId.toLowerCase())) return false;

    try {
      const response = await updateClientSyncSchedule(clientId, enabled, intervalHours);

      if (response.success && syncData) {
        setSyncData({
          ...syncData,
          syncSchedule: response.syncSchedule
        });
      }

      return response.success;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update schedule');
      return false;
    }
  }, [clientId, syncData]);

  // Load sync data on mount and when clientId changes
  useEffect(() => {
    if (clientId) {
      refresh();
    } else {
      setSyncData(null);
      setLoading(false);
      setError(null);
      setStaleness('never_synced');
      setNeedsSync(true);
    }
  }, [clientId, refresh]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    syncData,
    loading,
    syncing,
    error,
    staleness,
    needsSync,
    refresh,
    triggerSync,
    updateSchedule
  };
}

/**
 * Format a timestamp for display
 */
export function formatSyncTime(timestamp: string | null): string {
  if (!timestamp) return 'Never';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return date.toLocaleDateString();
}

/**
 * Get staleness badge color
 */
export function getStalenessColor(staleness: Staleness): string {
  switch (staleness) {
    case 'fresh': return 'text-green-600 dark:text-green-400';
    case 'stale': return 'text-yellow-600 dark:text-yellow-400';
    case 'very_stale': return 'text-orange-600 dark:text-orange-400';
    case 'never_synced': return 'text-gray-500 dark:text-gray-400';
  }
}
